import { GetJobLineItemQueryResponse, LineItemDetailsJson } from '@/api/jobs'
import { type PriceBookItemType, parsePriceBookItemType } from '@/api/types/priceBookTypes'
import { format, parseISO } from 'date-fns'
import { isEqual, omitBy } from 'lodash'

export interface LineItemDetails {
  /** Unset falls back to the price book item's description; `''` is a deliberate clear. */
  description?: string
  referenceNumber?: string
  serialNumber?: string
  storageLocation?: string
  /**
   * The worker's user uuid. On a new item, unset falls back to the job's sole
   * operator; `''` is a deliberate clear. Saved as `null` either way.
   */
  workerId?: string
  /** Unset falls back to the job's start date or today. */
  workDate?: Date
}

export function lineItemDetailsFromJson(
  json: LineItemDetailsJson | null | undefined,
): LineItemDetails {
  if (!json) return {}
  const details: LineItemDetails = {
    referenceNumber: json.reference_number ?? undefined,
    description: json.description ?? undefined,
    workerId: json.worker_id ?? undefined,
    // parseISO reads a zoneless timestamp as local time, so the calendar day survives.
    workDate: json.work_date ? parseISO(json.work_date) : undefined,
    storageLocation: json.storage_location ?? undefined,
    serialNumber: json.serial_number ?? undefined,
  }
  return omitBy(details, (value) => value === undefined)
}

/** Every field is sent, since the backend replaces the whole set; unset goes as `null`. */
export function lineItemDetailsToJson(details: LineItemDetails): LineItemDetailsJson {
  const json: LineItemDetailsJson = {
    reference_number: details.referenceNumber ?? null,
    description: details.description ?? null,
    // The worker id is a uuid, so a deliberate `''` clear can't be stored apart from unset.
    worker_id: details.workerId || null,
    // Date only: toISOString would shift the day for anyone west of UTC.
    work_date: details.workDate ? format(details.workDate, 'yyyy-MM-dd') : null,
    storage_location: details.storageLocation ?? null,
    serial_number: details.serialNumber ?? null,
  }
  return json
}

/** Compares what would be persisted, so an unsaved-only field like `unitNumber` isn't a change. */
export function lineItemDetailsEqual(a: LineItemDetails, b: LineItemDetails): boolean {
  return isEqual(lineItemDetailsToJson(a), lineItemDetailsToJson(b))
}

export class LineItem {
  constructor(json: GetJobLineItemQueryResponse) {
    this.id = json.id
    this.name = json.name
    this.rate = json.rate
    this.quantity = json.quantity
    this.unitOfMeasure = json.unit_of_measure
    this.created = json.created
    this.position = json.position
    this.priceBookItemId = json.price_book_item_id
    this.matchesRateAgreement = json.matches_rate_agreement
    this.type = parsePriceBookItemType(json.type)
    this.billable = json.billable ?? true
    this.details = lineItemDetailsFromJson(json.details)
  }

  id: string
  name: string
  rate: number | null
  quantity: number | null
  unitOfMeasure: string | null
  created: Date
  position: number | null
  priceBookItemId: string | null
  matchesRateAgreement: boolean | null
  /**
   * The type picked for a custom item. On a linked item it's the backend's copy
   * of the price book item's type; the price book item's own type is what counts.
   */
  type: PriceBookItemType | null
  /** Non-billable items are left out of the billable total. */
  billable: boolean
  details: LineItemDetails
}
